import type { ReactNode } from 'react';

import React, { createContext, useCallback, useContext, useState } from 'react';

interface HeaderContent {
  id: string;
  content: ReactNode;
  priority?: number;
}

interface HeaderContentContextType {
  headerContents: HeaderContent[];
  registerHeaderContent: (id: string, content: ReactNode, priority?: number) => void;
  unregisterHeaderContent: (id: string) => void;
}

const HeaderContentContext = createContext<HeaderContentContextType | undefined>(undefined);

export const useHeaderContent = () => {
  const context = useContext(HeaderContentContext);

  if (!context) {
    throw new Error('useHeaderContent must be used within a HeaderContentProvider');
  }

  return context;
};

export const HeaderContentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [headerContents, setHeaderContents] = useState<HeaderContent[]>([]);

  const registerHeaderContent = useCallback((id: string, content: ReactNode, priority = 0) => {
    setHeaderContents((prev) => {
      const filtered = prev.filter((item) => item.id !== id);
      const newContent = { id, content, priority };

      return [...filtered, newContent].sort((a, b) => (b.priority || 0) - (a.priority || 0));
    });
  }, []);

  const unregisterHeaderContent = useCallback((id: string) => {
    setHeaderContents((prev) => prev.filter((item) => item.id !== id));
  }, []);

  return (
    <HeaderContentContext.Provider
      value={{
        headerContents,
        registerHeaderContent,
        unregisterHeaderContent,
      }}
    >
      {children}
    </HeaderContentContext.Provider>
  );
};

export const HeaderContentRenderer: React.FC = () => {
  const { headerContents } = useHeaderContent();

  if (headerContents.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full items-center gap-2">
      {headerContents.map(({ id, content }) => (
        <React.Fragment key={id}>{content}</React.Fragment>
      ))}
    </div>
  );
};
